/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  FlatList,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Circle } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';
import { isWeb, isNative } from '@global/screens';
import {
  parseDate,
  hasNoAccess,
  cleanExpiredActivityTodoStorage,
} from '@global/helpers';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import * as Layout from '@components/screen/Layout';
import LoaderTotal from '@/screens/sales-activity/components/LoaderTotal';
import SalesActivityDetail from '@/screens/sales-activity/components/SalesActivityDetail';
import CreateNewPlan from '@/screens/sales-activity/components/CreateNewPlan';
import SelectNewPlanRole from '@/screens/sales-activity/components/SelectNewPlanRole';
import SalesActivityForm from '@/screens/sales-activity/components/SalesActivityForm';
import ModalUpload from '@components/modal/ModalUpload';
import templateImportSalesActivityId from '@/assets/csv/template_import_sales_activity_id';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import { logEventScreen } from '@global/analytics';
import { showPopup } from '@/store/modal';
import Toast from 'react-native-toast-message';

const dimension = Dimensions.get('window');
const { height: windowHeight } = Dimensions.get('window');

const activityTypes = {
  planned_activities: ['draft', 'waiting_confirmation', 'rejected'],
  overdue_activities: ['approved', 'waiting_confirmation'],
  past_activities: ['cancelled', 'finished'],
};

// Activity titles are persisted server-side as "<English prefix> - <customer name>"
// (see SalesActivityForm.js prefix_title), so translate() can never match the whole
// string. Split off the known prefix so SociollaText can translate just that part.
const ACTIVITY_TITLE_PREFIXES = [
  'Invoice Collection',
  'Engage Potential Customer',
  'Event Activity',
  'Visit',
];

const splitActivityTitle = (title) => {
  const matched = ACTIVITY_TITLE_PREFIXES.find(
    (prefix) => title === prefix || title.startsWith(`${prefix} - `),
  );
  return matched ? [matched, title.slice(matched.length)] : title;
};

const SalesActivityScreen = (props) => {
  const { route, navigation } = props;
  const allowedRoles = [
    'salesman',
    'beauty_advisor',
    'merchandiser',
    'promotor',
    'collector',
  ];
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.user);
  const showImportModalSalesActivity = useSelector(
    (state) => !!state.modal.showImportModalSalesActivity,
  );
  const userRole = userData?.role?.code;
  const allowedDailyGoalRoles = ['salesman', 'merchandiser'];
  const shouldShowDailyGoal = allowedDailyGoalRoles.includes(userRole);
  const shouldRenderDailyGoalInMobileHeader = !isWeb && shouldShowDailyGoal;

  const [showDetail, setShowDetail] = useState(false);
  const [detailId, setDetailId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showSelectRole, setShowSelectRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isLoadingTotal, setIsLoadingTotal] = useState(false);
  const [activityType, setActivityType] = useState(
    'visiting_existing_customer',
  );

  const [totalPlanned, setTotalPlanned] = useState(0);
  const [totalOverdue, setTotalOverdue] = useState(0);
  const [totalPast, setTotalPast] = useState(0);
  const [activityData, setActivityData] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [dailyGoal, setDailyGoal] = useState({ target: 0, actual: 0 });
  const [isLoadingDailyGoal, setIsLoadingDailyGoal] = useState(false);

  const onRefresh = async () => {
    try {
      setRefreshing(true);
      await doFetchTotal();
      await doFetchData();
    } finally {
      setRefreshing(false);
    }
  };

  const showNewPlan = !hasNoAccess([
    'salesman',
    'sales_supervisor',
    'branch_head',
    'superadmin',
    'merchandiser',
    'beauty_advisor',
    'general',
    'promotor',
    'collector',
  ]);

  const getActivities = useApi(MsB2b.getActivities);

  const handleImportSalesActivity = async (url) => {
    try {
      await MsB2b.importSalesActivityCsv({ url });
      Toast.show({
        text1: 'Import in progress, the result will be sent to your email.',
        type: 'success',
      });
      dispatch(
        showPopup({ name: 'showImportModalSalesActivity', value: false }),
      );
    } catch (error) {
      Toast.show({
        text1:
          error?.response?.data?.message ||
          error?.message ||
          'A system error occurred',
        type: 'error',
      });
    }
  };

  const doFetchTotal = async () => {
    setIsLoadingTotal(true);
    if (shouldShowDailyGoal) {
      setIsLoadingDailyGoal(true);
    }
    try {
      const keys = Object.keys(activityTypes);
      const doChangeTotal = (act, total = 0) => {
        if (act == 'planned_activities') {
          setTotalPlanned(total);
        } else if (act == 'overdue_activities') {
          setTotalOverdue(total);
        } else {
          setTotalPast(total);
        }
      };
      const countResponse = await MsB2b.getActivitiesCountAll();
      const countData = countResponse?.data?.data;
      if (countData && Object.keys(countData).length) {
        for (let i = 0; i < keys.length; i++) {
          const act = keys[i];
          doChangeTotal(act, countData[act]);
        }

        if (shouldShowDailyGoal) {
          setDailyGoal({
            target: countData?.target_activities ?? 0,
            actual: countData?.actual_activities ?? 0,
          });
        }
      } else {
        for (let i = 0; i < keys.length; i++) {
          const act = keys[i];
          doChangeTotal(act, 0);
        }
        if (shouldShowDailyGoal) {
          setDailyGoal({ target: 0, actual: 0 });
        }
      }
      setTimeout(() => {
        setIsLoadingTotal(false);
        if (shouldShowDailyGoal) {
          setIsLoadingDailyGoal(false);
        }
      }, 250);
    } catch (e) {
      setIsLoadingTotal(false);
      if (shouldShowDailyGoal) {
        setDailyGoal({ target: 0, actual: 0 });
        setIsLoadingDailyGoal(false);
      }
    }
  };

  const cleanOldAsyncStorage = async () => {
    try {
      await cleanExpiredActivityTodoStorage(2);
    } catch (err) {
      console.error('Error cleaning AsyncStorage:', err);
    }
  };

  const doFetchData = async () => {
    try {
      const response = await getActivities.request({
        params: {
          filter: {
            type: 'upcoming_activities',
          },
          sort: 'visited_at',
        },
      });
      if (response?.data?.data) {
        setActivityData(response.data.data);
      } else {
        setActivityData([]);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const openForm = (val) => {
    setActivityType(val);
    setShowCreate(false);
    setShowSelectRole(false);
    setShowForm(true);
  };

  const handleDetail = (id = '', shouldRefresh = false) => {
    if (id) {
      logEventScreen('SalesActivityDetailScreen');
      setDetailId(id);
      setTimeout(() => {
        setShowDetail(id ? true : false);
      }, 250);
    } else {
      if (detailId && showDetail) {
        setDetailId('');
        setShowDetail(false);
        props.navigation.setParams({ id: '', direct: '' });
      }
      if (shouldRefresh) {
        doFetchTotal();
        doFetchData();
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      const id = props?.route?.params?.id;
      const direct = props?.route?.params?.direct;
      if (id && direct) {
        // Coming back from a child screen (e.g. from Order detail).
        // Open the modal immediately without the 250 ms animation delay so
        // the user is taken straight to the correct tab instead of seeing the
        // activity list briefly before the modal slides in.
        logEventScreen('SalesActivityDetailScreen');
        setDetailId(id);
        setShowDetail(true);
      } else {
        handleDetail(id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props.route.params]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setActivityType('visiting_existing_customer');
      setTotalPlanned(0);
      setTotalOverdue(0);
      setTotalPast(0);

      doFetchTotal();
      doFetchData();
      cleanOldAsyncStorage();
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  useEffect(() => {
    if (!shouldShowDailyGoal) {
      setDailyGoal({ target: 0, actual: 0 });
      setIsLoadingDailyGoal(false);
    }
  }, [shouldShowDailyGoal]);

  const goToList = (type, title) => {
    props.navigation.navigate('SalesActivityListScreen', {
      type: type,
      titleScreen: title,
    });
  };

  const getTabContentStyle = () => ({
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    ...(!isWeb
      ? {
          paddingHorizontal: 15,
          ...(shouldRenderDailyGoalInMobileHeader
            ? {}
            : {
                paddingBottom: 15,
                borderBottomColor: Colors.grey0,
                borderBottomWidth: 1,
                marginBottom: 0,
              }),
          ...(!showNewPlan ? { marginTop: 10 } : {}),
        }
      : {}),
  });

  const _getCardProps = (type, activity, activityTitle) => ({
    type,
    style: { width: '32%' },
    touchable: true,
    onPress: () => goToList(activity, activityTitle),
  });

  const renderCardTitle = (total) => (
    <SociollaText type="medium" style={styles.cardTitle}>
      {total}
    </SociollaText>
  );

  const renderCardDescription = (description) => (
    <SociollaText type="medium" style={styles.cardDesc}>
      {description}
    </SociollaText>
  );

  const renderCardContent = (isLoading, total) => {
    return isLoading ? <LoaderTotal /> : renderCardTitle(total);
  };

  const renderPlannedActivitiesCard = () => (
    <Layout.Card
      testID="sales-activity-planned-card"
      {..._getCardProps('primary', 'planned_activities', 'Planned Activities')}>
      {renderCardContent(isLoadingTotal, totalPlanned)}
      {renderCardDescription('Planned Activities')}
    </Layout.Card>
  );

  const renderOverdueActivitiesCard = () => (
    <Layout.Card
      testID="sales-activity-overdue-card"
      {..._getCardProps('danger', 'overdue_activities', 'Overdue Activities')}>
      {renderCardContent(isLoadingTotal, totalOverdue)}
      {renderCardDescription('Overdue Activities')}
    </Layout.Card>
  );

  const renderPastActivitiesCard = () => (
    <Layout.Card
      testID="sales-activity-past-card"
      {..._getCardProps('warning', 'past_activities', 'Past Activities')}>
      {renderCardContent(isLoadingTotal, totalPast)}
      {renderCardDescription('Past Activities')}
    </Layout.Card>
  );

  const TabContent = () => (
    <View testID="sales-activity-tab-content" style={getTabContentStyle()}>
      {renderPlannedActivitiesCard()}
      {renderOverdueActivitiesCard()}
      {renderPastActivitiesCard()}
    </View>
  );

  const renderTitleScreen = () => (
    <Layout.Title testID="sales-activity-title" title="Sales Activity" />
  );

  const handlePlanNewActivityPress = () => {
    logEventScreen('PlanNewActivityScreen');
    if (!allowedRoles.includes(userRole)) {
      setShowCreate(true);
    } else {
      setSelectedRole(userRole);
      setShowCreate(true);
    }
  };

  const renderPlanNewActivityTitle = () => (
    <SociollaText type="medium" style={styles.planNewActivityTitle}>
      Plan New Activity
    </SociollaText>
  );

  const getButtonNewPlanProps = () => ({
    testID: 'sales-activity-plan-new-btn',
    rounded: true,
    buttonStyle: styles.fullWidth,
    title: renderPlanNewActivityTitle(),
    onPress: handlePlanNewActivityPress,
  });

  const renderNewActivityPlanButton = () => (
    <View
      testID="sales-activity-new-plan-wrapper"
      style={styles.newPlanContainerButton}>
      <Button {...getButtonNewPlanProps()} />
    </View>
  );

  const renderImportSalesActivityTitle = () => (
    <SociollaText type="medium" style={styles.importSalesActivityTitle}>
      Import CSV
    </SociollaText>
  );

  const renderImportSalesActivityButton = (testID) => (
    <View
      testID={testID}
      style={{
        paddingHorizontal: 15,
        paddingBottom: 15,
      }}>
      <Button
        rounded
        type="default"
        buttonStyle={styles.fullWidth}
        title={renderImportSalesActivityTitle()}
        onPress={() =>
          dispatch(
            showPopup({ name: 'showImportModalSalesActivity', value: true }),
          )
        }
      />
    </View>
  );

  const renderScrollableMobile = () => (
    <React.Fragment>
      {showNewPlan && renderNewActivityPlanButton()}
      {showNewPlan &&
        renderImportSalesActivityButton('sales-activity-import-mobile-wrapper')}
      <TabContent />
      {shouldRenderDailyGoalInMobileHeader && (
        <>
          <View style={styles.dailyGoalMobileWrapper}>
            {renderDailyGoalCard()}
          </View>
          <View style={styles.separatorLine} />
        </>
      )}
    </React.Fragment>
  );

  const _getScrollableProps = () => ({
    onCallback: () => {
      doFetchTotal();
      doFetchData();
    },
  });

  const getCardStyle = () => ({
    maxWidth: 550,
    padding: 0,
    ...(!isWeb ? styles.containerLayoutCard : {}),
  });

  const _getActivityItemProps = (item, idx) => ({
    onPress: () => handleDetail(item._id),
    style: styles.activityItemTouchable,
  });

  const renderActivityDate = (item) => (
    <View style={styles.activityDate}>
      <LinearGradient
        colors={['#A8D6F2', '#A8D6F2', '#7AA0E1']}
        locations={[0.3388, 0.485, 0.81]}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={styles.activityDateGradient}>
        <SociollaText type="medium" style={{ fontSize: 16 }}>
          {parseDate(item.visited_at, 'dd')}
        </SociollaText>
        <SociollaText style={{ fontSize: 12 }}>
          {parseDate(item.visited_at, 'MMM')}
        </SociollaText>
      </LinearGradient>
    </View>
  );

  const renderActivityDateContainer = (item) => (
    <View style={styles.activityDateContainer}>{renderActivityDate(item)}</View>
  );

  const getActivityUserTag = (item) => {
    const roleCode = item?.user?.role || item?.user?.role?.code;
    const userName = item?.user?.name || item?.user_name;
    if (!roleCode || !userName) {
      return '';
    }

    const roleLabels = {
      salesman: 'Salesman',
      beauty_advisor: 'Beauty Advisor',
      merchandiser: 'Merchandiser',
    };

    const formatFallbackRole = (code) =>
      code.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

    const formattedRole = roleLabels[roleCode] || formatFallbackRole(roleCode);
    return `${formattedRole} - ${userName}`;
  };

  const renderActivityTime = (item) => {
    return item?.start_time && item?.end_time
      ? `${item?.start_time} - ${item?.end_time}`
      : `-`;
  };

  const renderActivityDetails = (item) => {
    const title = item?.title || '';
    const address = item?.customer?.address?.name || '';
    const shouldShowAddress =
      item?.activity_type !== 'engage_potential_customers' || Boolean(address);
    const userTag = getActivityUserTag(item);

    return (
      <View style={styles.activityDetails}>
        <SociollaText style={styles.activityDetailsText}>
          {renderActivityTime(item)}
        </SociollaText>
        <SociollaText style={{ fontSize: 13 }}>
          {splitActivityTitle(title)}
        </SociollaText>
        {shouldShowAddress ? (
          <SociollaText
            style={{
              fontSize: isWeb ? 13 : 12,
              marginTop: 5,
            }}>
            {address}
          </SociollaText>
        ) : null}
        {userTag ? (
          <SociollaText style={styles.activityUserTag}>{userTag}</SociollaText>
        ) : null}
      </View>
    );
  };

  const renderActivityItem = (item, idx) => (
    <TouchableOpacity
      testID={`sales-activity-item-${idx}`}
      key={idx}
      {..._getActivityItemProps(item, idx)}>
      {renderActivityDateContainer(item)}
      {renderActivityDetails(item)}
    </TouchableOpacity>
  );

  const renderUpcomingActivitiesTitle = () => (
    <SociollaText type="medium" style={{ fontSize: 18 }}>
      Upcoming Activities
    </SociollaText>
  );

  const _getSeeAllButtonProps = () => ({
    testID: 'sales-activity-see-all-btn',
    activeOpacity: 0.6,
    onPress: () => goToList('upcoming_activities', 'Upcoming Activities'),
  });

  const renderSeeAllUpcomingActivitiesButton = () => (
    <TouchableOpacity {..._getSeeAllButtonProps()}>
      <SociollaText style={{ color: Colors.blue0 }}>see all</SociollaText>
    </TouchableOpacity>
  );

  const renderUpcomingActivitiesHeader = () => (
    <View
      testID="sales-activity-upcoming-header"
      style={styles.upcomingActivitiesHeader}>
      {renderUpcomingActivitiesTitle()}
      {renderSeeAllUpcomingActivitiesButton()}
    </View>
  );

  const renderNoActivityFound = () => (
    <View testID="sales-activity-no-data" style={styles.noActivityFoundContent}>
      <SociollaText>No activity found</SociollaText>
    </View>
  );

  const renderActivityList = () => {
    if ((getActivities.loading || isLoadingTotal) && activityData.length > 0) {
      return (
        <View>
          {!isWeb && renderScrollableMobile()}
          {renderUpcomingActivitiesHeader()}

          <View style={styles.activityItemTouchable}>
            <Layout.Loader />
          </View>
        </View>
      );
    }

    return (
      <FlatList
        data={activityData}
        keyExtractor={(it, idx) => String(it?._id ?? idx)}
        renderItem={({ item, index }) => renderActivityItem(item, index)}
        ListHeaderComponent={
          <View>
            {!isWeb && renderScrollableMobile()}
            {renderUpcomingActivitiesHeader()}
          </View>
        }
        ListEmptyComponent={renderNoActivityFound}
        refreshing={refreshing}
        onRefresh={onRefresh}
        contentContainerStyle={{
          paddingBottom: 50,
        }}
      />
    );
  };

  const renderCard = () => (
    <Layout.Card style={getCardStyle()}>
      <View style={styles.fullWidth}>{renderActivityList()}</View>
    </Layout.Card>
  );

  const renderDailyGoalCard = () => {
    if (!shouldShowDailyGoal) {
      return null;
    }

    const target = dailyGoal?.target ?? 0;
    const actual = dailyGoal?.actual ?? 0;
    const hasTarget = target > 0;
    const hasActualWithoutTarget = !hasTarget && actual > 0;
    const progress = hasTarget
      ? Math.min(actual / target, 1)
      : hasActualWithoutTarget
      ? 1
      : 0;
    const chartSize = isWeb ? 140 : 100;
    const strokeWidth = 10;
    const radius = (chartSize - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - progress * circumference;
    const ratioDisplay = `${actual}/${target}`;

    return (
      <Layout.Card
        testID="sales-activity-daily-goal-card"
        style={styles.dailyGoalCard}>
        <SociollaText type="medium" style={styles.dailyGoalTitle}>
          Daily Activities Goal
        </SociollaText>
        {isLoadingDailyGoal ? (
          <View style={styles.dailyGoalLoader}>
            <Layout.Loader />
          </View>
        ) : (
          <View style={styles.dailyGoalContent}>
            <View style={styles.dailyGoalChartWrapper}>
              {!isNative ? (
                <svg
                  width={chartSize}
                  height={chartSize}
                  viewBox={`0 0 ${chartSize} ${chartSize}`}>
                  <circle
                    cx={chartSize / 2}
                    cy={chartSize / 2}
                    r={radius}
                    stroke={Colors.grey0}
                    strokeWidth={strokeWidth}
                    fill="none"
                  />
                  <circle
                    cx={chartSize / 2}
                    cy={chartSize / 2}
                    r={radius}
                    stroke={Colors.blue0}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={`${circumference} ${circumference}`}
                    strokeDashoffset={dashOffset}
                    fill="none"
                    transform={`rotate(-90 ${chartSize / 2} ${chartSize / 2})`}
                  />
                </svg>
              ) : (
                <Svg
                  width={chartSize}
                  height={chartSize}
                  viewBox={`0 0 ${chartSize} ${chartSize}`}>
                  <Circle
                    cx={chartSize / 2}
                    cy={chartSize / 2}
                    r={radius}
                    stroke={Colors.grey0}
                    strokeWidth={strokeWidth}
                    fill="none"
                  />
                  <Circle
                    cx={chartSize / 2}
                    cy={chartSize / 2}
                    r={radius}
                    stroke={Colors.blue0}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={`${circumference} ${circumference}`}
                    strokeDashoffset={dashOffset}
                    fill="none"
                    transform={`rotate(-90 ${chartSize / 2} ${chartSize / 2})`}
                  />
                </Svg>
              )}
              <View
                style={[
                  styles.dailyGoalRatioWrapper,
                  { width: chartSize, height: chartSize },
                ]}>
                <SociollaText type="medium" style={styles.dailyGoalRatioText}>
                  {ratioDisplay}
                </SociollaText>
              </View>
            </View>
          </View>
        )}
      </Layout.Card>
    );
  };

  const renderLayoutWrapper = (children) => (
    <Layout.Wrapper white={false}>
      <View style={styles.fullWidth}>{children}</View>
    </Layout.Wrapper>
  );

  const renderMainContent = () => {
    if (isWeb) {
      return (
        <View style={styles.desktopContentRow}>
          <View
            style={[
              styles.desktopUpcomingWrapper,
              !shouldShowDailyGoal ? { marginRight: 0 } : {},
            ]}>
            {renderCard()}
          </View>
          {shouldShowDailyGoal ? (
            <View style={styles.desktopGoalWrapper}>
              {renderDailyGoalCard()}
            </View>
          ) : null}
        </View>
      );
    }

    return <View>{renderCard()}</View>;
  };

  const renderTabContentAndCard = () => (
    <View
      style={{
        ...(isWeb
          ? {}
          : {
              overflow: 'scroll',
              maxHeight: windowHeight - 80,
            }),
      }}>
      {isWeb && <TabContent />}
      {renderMainContent()}
    </View>
  );

  const onPressPlanNewActivity = () => {
    if (!allowedRoles.includes(userRole)) {
      setShowCreate(true);
    } else {
      setSelectedRole(userRole);
      setShowCreate(true);
    }
  };

  const _getPlanNewActivityButtonProps = () => ({
    testID: 'sales-activity-plan-new-web-btn',
    rounded: true,
    buttonStyle: styles.fullWidth,
    title: renderPlanNewActivityTitle(),
    onPress: onPressPlanNewActivity,
  });

  const renderPlanNewActivityButton = () => (
    <View
      testID="sales-activity-plan-new-web-wrapper"
      style={styles.planNewActivityButtonWrapper}>
      <Button {..._getPlanNewActivityButtonProps()} />
    </View>
  );

  const renderWebActionButtons = () => {
    if (!showNewPlan) {
      return null;
    }

    return (
      <View style={styles.webActionButtons}>
        {showNewPlan ? (
          <View testID="sales-activity-import-web-wrapper">
            <Button
              testID="sales-activity-import-web-btn"
              rounded
              type="default"
              buttonStyle={styles.fullWidth}
              title={renderImportSalesActivityTitle()}
              onPress={() =>
                dispatch(
                  showPopup({
                    name: 'showImportModalSalesActivity',
                    value: true,
                  }),
                )
              }
            />
          </View>
        ) : null}
        {showNewPlan ? renderPlanNewActivityButton() : null}
      </View>
    );
  };

  const renderLayoutWrapperContent = () => (
    <View style={styles.containerLayoutWrapper}>
      {renderLayoutWrapper(renderTabContentAndCard())}
      {isWeb ? renderWebActionButtons() : null}
    </View>
  );

  const renderScrollViewRefresher = () => {
    if (isWeb || !isNative) {
      return (
        <Layout.ScrollViewRefresher {..._getScrollableProps()}>
          {renderLayoutWrapperContent()}
        </Layout.ScrollViewRefresher>
      );
    }

    return renderActivityList();
  };

  const renderLayoutContainer = () => (
    <Layout.Container testID="sales-activity-screen">
      {isWeb && renderTitleScreen()}
      {renderScrollViewRefresher()}
    </Layout.Container>
  );

  const renderSalesActivityDetail = () => (
    <SalesActivityDetail
      id={detailId}
      show={showDetail}
      route={route}
      navigation={navigation}
      onClose={(shouldRefresh) => handleDetail('', shouldRefresh)}
    />
  );

  const renderCreateNewPlan = () => (
    <CreateNewPlan
      show={showCreate}
      selectedRole={selectedRole}
      showRoleSelection={!allowedRoles.includes(userRole)}
      onClose={() => setShowCreate(false)}
      onSubmit={(val) => {
        if (val && typeof val === 'object') {
          if (val.role) {
            setSelectedRole(val.role);
          }
          openForm(val.activityType);
        } else {
          openForm(val);
        }
      }}
    />
  );

  const renderSelectNewPlanRole = () => (
    <SelectNewPlanRole
      show={showSelectRole}
      onClose={() => setShowSelectRole(false)}
      onSubmit={(val) => {
        setSelectedRole(val);
        setShowSelectRole(false);
        setShowCreate(true);
      }}
    />
  );

  const renderSalesActivityForm = () => (
    <SalesActivityForm
      activityType={activityType}
      show={showForm}
      selectedRole={selectedRole}
      onClose={() => setShowForm(false)}
      onSubmit={() => {
        setShowForm(false);
        doFetchTotal();
        doFetchData();
      }}
    />
  );

  const renderAllContent = () => (
    <React.Fragment>
      {renderLayoutContainer()}
      {renderSalesActivityDetail()}
      {renderCreateNewPlan()}
      {renderSelectNewPlanRole()}
      {renderSalesActivityForm()}
      {showImportModalSalesActivity ? (
        <ModalUpload
          testID="sales-activity-import-modal"
          show={showImportModalSalesActivity}
          onClose={() =>
            dispatch(
              showPopup({ name: 'showImportModalSalesActivity', value: false }),
            )
          }
          templateUrl={templateImportSalesActivityId}
          onUpload={handleImportSalesActivity}
          autoUploadS3={true}
          name="sales_activity"
          validHeader={[
            'activity_type',
            'user',
            'address_id',
            'date',
            'time_from',
            'time_to',
            'notes',
          ]}
        />
      ) : null}
    </React.Fragment>
  );

  return <React.Fragment>{renderAllContent()}</React.Fragment>;
};

const styles = StyleSheet.create({
  cardTitle: { fontSize: 24, marginBottom: 10 },
  cardDesc: { fontSize: isWeb ? 18 : 16, color: Colors.grey1 },
  newPlanContainerButton: { width: '100%', padding: 10 },
  planNewActivityTitle: { fontSize: 14, color: Colors.white },
  importSalesActivityTitle: { fontSize: 14, color: Colors.black },
  containerLayoutWrapper: {
    width: '100%',
    maxWidth: 1024,
    flexDirection: isWeb ? 'row' : 'column',
    justifyContent: 'space-between',
  },
  fullWidth: { width: '100%' },
  // eslint-disable-next-line react-native/no-color-literals
  containerLayoutCard: { borderTopColor: 'transparent' },
  upcomingActivitiesHeader: {
    width: '100%',
    padding: 15,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  noActivityFoundContent: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  activityItemTouchable: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  activityDate: {
    width: 62,
    height: 62,
    padding: 5,
    borderRadius: 5,
  },
  activityDateGradient: {
    width: '100%',
    height: '100%',
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 3,
  },
  activityDateContainer: {
    width: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityDetails: {
    paddingHorizontal: 10,
    width: !isNative ? 'calc(100% - 60px)' : dimension.width - 75,
  },
  activityDetailsText: {
    fontSize: 13,
    color: Colors.grey1,
    marginBottom: 5,
  },
  activityUserTag: {
    fontSize: 13,
    color: '#5D6DB8',
    marginTop: 4,
  },
  webActionButtons: {
    width: '18%',
    maxWidth: 225,
    gap: 10,
  },
  planNewActivityButtonWrapper: { width: '100%' },
  dailyGoalCard: {
    width: isWeb ? '100%' : '100%',
    paddingHorizontal: 20,
    paddingTop: 20,
    display: 'flex',
    flexDirection: isWeb ? 'column' : 'row',
    alignItems: 'center',
    gap: 30,
    paddingBottom: 0,
  },
  dailyGoalTitle: {
    fontSize: isWeb ? 18 : 16,
    marginBottom: isWeb ? 0 : 20,
  },
  dailyGoalLoader: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 140,
  },
  dailyGoalContent: {
    alignItems: 'center',
  },
  dailyGoalChartWrapper: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  dailyGoalRatioWrapper: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dailyGoalRatioText: {
    fontSize: isWeb ? 22 : 16,
  },
  desktopContentRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  desktopUpcomingWrapper: {
    flex: 1,
    marginRight: isWeb ? 20 : 0,
  },
  desktopGoalWrapper: {
    width: isWeb ? 320 : '100%',
  },
  dailyGoalMobileWrapper: {
    width: '100%',
    paddingHorizontal: 15,
  },
  separatorLine: {
    height: 1,
    backgroundColor: Colors.grey0,
    marginBottom: 20,
  },
});

export default SalesActivityScreen;
